import type { ModerationResult } from '@rover/rsdk/src/apiClient/latest/model/moderationResult';

/** One policy a photo broke. `ref` is a Cinder `customer_ref` or a Rekognition tag label. */
export type PhotoModerationReason = {
  ref: string;
  isIllegal: boolean;
};

export type PhotoModerationSurface = 'profile' | 'gallery';

export type PhotoModerationCheckFailureCause = 'timed_out' | 'tagging_failed';

export type PhotoModerationOutcome =
  | { status: 'passed' }
  | { status: 'rejected'; reasons: PhotoModerationReason[] }
  | { status: 'check_failed'; cause: PhotoModerationCheckFailureCause };

// Recorded when moderation itself could not run; the sitter should retry, not change photos.
export const IMAGE_TAGGING_FAILED_LABEL = 'image_tagging_failed';

// Stands in for a rejection that cites no policy, such as a Cinder policy without a `customer_ref`.
export const UNSPECIFIED_POLICY_REF = 'unspecified';

type ReasonEntry = Record<string, unknown>;

const isRecord = (value: unknown): value is ReasonEntry =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

// Both surfaces read results through the camelizing v7 client; snake_case keys are a fallback.
const field = (entry: ReasonEntry, snake: string, camel: string): unknown =>
  entry[snake] ?? entry[camel];

/** Mirrors `moderation.utils.load_reasons`: Rekognition rows store a JSON string, Cinder rows a list. */
export const loadModerationReasons = (reasons: unknown): ReasonEntry[] => {
  let parsed = reasons;
  if (typeof reasons === 'string') {
    try {
      parsed = JSON.parse(reasons);
    } catch {
      return [];
    }
  }
  return Array.isArray(parsed) ? parsed.filter(isRecord) : [];
};

const dedupeByRef = (reasons: PhotoModerationReason[]): PhotoModerationReason[] => {
  const seen = new Set<string>();
  return reasons.filter(({ ref }) => {
    if (seen.has(ref)) return false;
    seen.add(ref);
    return true;
  });
};

const reasonsFromDecision = (decision: ReasonEntry): PhotoModerationReason[] => {
  const { policies } = decision;
  if (Array.isArray(policies)) {
    return policies
      .filter(isRecord)
      .filter((policy) => field(policy, 'is_non_violating', 'isNonViolating') !== true)
      .map((policy) => ({
        ref: field(policy, 'customer_ref', 'customerRef'),
        isIllegal: field(policy, 'is_illegal', 'isIllegal') === true,
      }))
      .filter((reason): reason is PhotoModerationReason => isNonEmptyString(reason.ref));
  }

  const refs = field(decision, 'policy_refs', 'policyRefs');
  if (Array.isArray(refs)) {
    return refs.filter(isNonEmptyString).map((ref) => ({ ref, isIllegal: false }));
  }
  return [];
};

const isDecision = (entry: ReasonEntry): boolean =>
  'policies' in entry || 'policy_refs' in entry || 'policyRefs' in entry;

/**
 * The policies behind a failed result, deduplicated. Rekognition rows list one tag per
 * entry; Cinder rows append one entry per decision, and the latest decision stands.
 */
export const getPhotoModerationReasons = (
  reasons: ModerationResult['reasons'] | unknown
): PhotoModerationReason[] => {
  const entries = loadModerationReasons(reasons);

  const labels = entries.map((entry) => entry.label).filter(isNonEmptyString);
  if (labels.length) {
    return dedupeByRef(labels.map((ref) => ({ ref, isIllegal: false })));
  }

  const decision = [...entries].reverse().find(isDecision);
  return decision ? dedupeByRef(reasonsFromDecision(decision)) : [];
};

/** What the sitter should see for a polled result; `null` means the polling budget ran out. */
export const classifyModerationResult = (
  result: Pick<ModerationResult, 'status' | 'reasons'> | null | undefined
): PhotoModerationOutcome => {
  if (!result || result.status === 'pending') {
    return { status: 'check_failed', cause: 'timed_out' };
  }
  if (result.status !== 'failed') {
    return { status: 'passed' };
  }

  const reasons = getPhotoModerationReasons(result.reasons);
  if (reasons.some(({ ref }) => ref === IMAGE_TAGGING_FAILED_LABEL)) {
    return { status: 'check_failed', cause: 'tagging_failed' };
  }
  return {
    status: 'rejected',
    reasons: reasons.length ? reasons : [{ ref: UNSPECIFIED_POLICY_REF, isIllegal: false }],
  };
};
